import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, Sparkles, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { loadRazorpayScript } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";

const PLAN_META = {
  basic: { icon: Zap, accent: "from-blue-500 to-brand", tag: "Most flexible" },
  annual: { icon: Sparkles, accent: "from-brand to-indigo-700", tag: "Best value" },
};

export const PlansModal = ({ open, onOpenChange }) => {
  const { setUser, checkAuth } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/plans");
      setPlans(data.plans);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  // Load plans whenever the modal opens.
  if (open && loading && plans.length === 0) load();

  const subscribe = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/subscriptions/subscribe", { plan: planId });

      if (data.status === "activated") {
        // Demo mode (no Razorpay keys yet) — credits granted immediately.
        setUser(data.user);
        toast.success("Plan activated! Credits added to your account.");
        onOpenChange(false);
        return;
      }

      // Real Razorpay subscription checkout.
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error("Could not load payment gateway. Try again.");
        return;
      }
      const rzp = new window.Razorpay({
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: "IndiaBusinessCircle",
        description: `${planId === "annual" ? "Annual" : "Basic"} subscription`,
        theme: { color: "#0F3D8C" },
        handler: async (resp) => {
          try {
            const { data: v } = await api.post("/subscriptions/verify", {
              plan: planId,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setUser(v.user);
            toast.success("Subscription active! Credits added.");
            onOpenChange(false);
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed");
          }
        },
        modal: { ondismiss: () => checkAuth() },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="plans-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Buy Credits</DialogTitle>
          <DialogDescription>Each feed post costs 1 credit. Choose a plan that fits your business.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5 pt-2">
            {plans.map((p) => {
              const meta = PLAN_META[p.id] || PLAN_META.basic;
              const Icon = meta.icon;
              return (
                <div key={p.id} data-testid={`plan-card-${p.id}`} className="relative rounded-2xl border border-slate-200 p-6 flex flex-col hover:border-brand/40 transition-colors">
                  <span className={`absolute -top-3 left-6 rounded-full bg-gradient-to-r ${meta.accent} px-3 py-0.5 text-[11px] font-bold text-white`}>{meta.tag}</span>
                  <div className="h-11 w-11 rounded-xl bg-brand-light flex items-center justify-center mb-4">
                    <Icon size={22} className="text-brand" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-slate-900">{p.name}</h3>
                  <p className="mt-1 font-display font-black text-2xl text-brand">{p.price_label}</p>
                  <ul className="mt-4 space-y-2 text-sm text-slate-600 flex-1">
                    <li className="flex items-start gap-2"><Check size={16} className="text-[#22C55E] mt-0.5 shrink-0" /> {p.credits_label}</li>
                    <li className="flex items-start gap-2"><Check size={16} className="text-[#22C55E] mt-0.5 shrink-0" /> 1 credit per feed post</li>
                    <li className="flex items-start gap-2"><Check size={16} className="text-[#22C55E] mt-0.5 shrink-0" /> Auto-renews, cancel anytime</li>
                  </ul>
                  <Button data-testid={`subscribe-${p.id}-btn`} onClick={() => subscribe(p.id)} disabled={busy === p.id} className="mt-6 w-full h-11 rounded-lg bg-brand hover:bg-brand-hover font-semibold gap-2">
                    {busy === p.id && <Loader2 className="animate-spin" size={16} />} Choose {p.name}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
